import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import PhotoEvidenceUpload from "../../components/PhotoEvidenceUpload";
import { inspectionRequest, errorMessage, newRequestId } from "./inspectionApi";
import { categories, RATINGS, validateStep } from "./inspectionUi";

export default function InspectionForm({ onCreated, onCancel }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState(() => ({
    machineId: "",
    machineType: "snack",
    checklist: categories("snack").map(({ key }) => ({
      key,
      rating: "",
      note: "",
    })),
    photos: [],
    rating: "",
    comment: "",
    requestId: newRequestId(),
  }));
  const [machines, setMachines] = useState([]);
  const [machine, setMachine] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [optionsError, setOptionsError] = useState("");
  useEffect(() => {
    let active = true;
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await inspectionRequest(
          `/options?search=${encodeURIComponent(search)}`
        );
        if (active) {
          setMachines(result.machines);
          setOptionsError("");
        }
      } catch (err) {
        if (active) setOptionsError(errorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [search]);
  const update = (key, value) =>
    setDraft((prev) => ({ ...prev, [key]: value }));
  const selectMachine = (value) => {
    setMachine(value);
    setDraft((prev) => ({
      ...prev,
      machineId: value._id,
      machineType: value.inspectionType || (/coffee|caf[eé]|jetinno/i.test(value.type || "") ? "coffee" : "snack"),
    }));
  };
  const submit = async () => {
    for (let index = 0; index <= 2; index++) {
      const message = validateStep(index, draft);
      if (message) {
        setStep(index);
        setError(message);
        return;
      }
    }
    setSaving(true);
    setError("");
    try {
      const result = await inspectionRequest("", draft);
      onCreated(result);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const next = () => {
    const message = validateStep(step, draft);
    setError(message);
    if (!message) setStep(step + 1);
  };
  return (
    <div className="mi-form">
      <div className="mi-steps" aria-label="Progreso de la inspección">
        {["Máquina", "Evaluación", "Evidencia y envío"].map((label, index) => (
          <div
            key={label}
            className={index <= step ? "is-active" : ""}
            aria-current={index === step ? "step" : undefined}
          >
            <span>
              {index < step ? <i className="pi pi-check" /> : index + 1}
            </span>
            <strong>{label}</strong>
          </div>
        ))}
      </div>
      {error && (
        <div className="mi-error" role="alert">
          {error}
        </div>
      )}
      {step === 0 && (
        <section className="mi-form-section">
          <div className="mi-section-heading">
            <span className="mi-icon">
              <i className="pi pi-desktop" />
            </span>
            <div>
              <h3>¿Qué máquina vas a inspeccionar?</h3>
              <p>Busca por nombre, serie, ubicación o ruta.</p>
            </div>
          </div>
          <label htmlFor="mi-machine">
            Máquina <span aria-hidden="true">*</span>
          </label>
          <Dropdown
            inputId="mi-machine"
            value={machine}
            options={
              machine && !machines.some((m) => m._id === machine._id)
                ? [machine, ...machines]
                : machines
            }
            dataKey="_id"
            optionLabel="serialNumber"
            filter
            filterBy="machine,serialNumber,location,route"
            onFilter={(e) => setSearch(e.filter)}
            onChange={(e) => selectMachine(e.value)}
            placeholder={
              loading
                ? "Buscando máquinas…"
                : "Buscar y seleccionar una máquina"
            }
            emptyFilterMessage="No se encontraron máquinas"
            emptyMessage="No hay máquinas disponibles"
            className="w-full"
            itemTemplate={(m) => (
              <div className="mi-machine-option">
                <strong>{m.machine || m.serialNumber}</strong>
                <small>
                  {m.serialNumber} · {m.location || "Sin ubicación"} ·{" "}
                  {m.route || "Sin ruta"}
                </small>
              </div>
            )}
            valueTemplate={(m) =>
              m ? (
                <span>
                  {m.machine || m.serialNumber} · {m.serialNumber}
                </span>
              ) : (
                <span>Buscar y seleccionar una máquina</span>
              )
            }
          />
          {optionsError && (
            <p role="alert" className="mi-error">
              {optionsError}
            </p>
          )}
          {machine && (
            <>
              <div className="mi-machine-context">
                <div>
                  <small>UBICACIÓN</small>
                  <strong>{machine.location || "Sin ubicación"}</strong>
                </div>
                <div>
                  <small>RUTA</small>
                  <strong>{machine.route || "Sin ruta"}</strong>
                </div>
                <div>
                  <small>RESPONSABLE REGISTRADO</small>
                  <strong>
                    <i className="pi pi-user" />{" "}
                    {machine.operator?.name || "Sin responsable"}
                  </strong>
                </div>
              </div>
              {!machine.operator && !machine.assignmentWarning && (
                <div className="mi-notice">
                  <i className="pi pi-info-circle" /> No se encontró un operador
                  único y activo. Supervisión recibirá el aviso para asignarlo.
                </div>
              )}
            </>
          )}
          {machine?.inspectionType ? (
            <div className="mi-machine-context" role="status">
              <div><small>TIPO DE MÁQUINA</small><strong>{machine.inspectionType === "coffee" ? "Café" : "Snack"}</strong></div>
            </div>
          ) : machine ? <fieldset className="mi-type">
            <legend>Tipo de evaluación (sin tipo Café/Snack registrado)</legend>
            {[
              ["snack", "Snack", "pi pi-box"],
              ["coffee", "Café", "pi pi-coffee"],
            ].map(([value, label, icon]) => (
              <button
                type="button"
                key={value}
                aria-pressed={draft.machineType === value}
                className={draft.machineType === value ? "is-selected" : ""}
                onClick={() => update("machineType", value)}
              >
                <i className={icon} />
                {label}
              </button>
            ))}
          </fieldset> : null}
        </section>
      )}
      {step === 1 && (
        <section className="mi-form-section">
          <h3>Observa, evalúa y describe</h3>
          <p className="mi-muted">
            Marca solo lo que hayas podido revisar. Describe cada hallazgo
            regular o malo.
          </p>
          <div className="mi-checklist">
            {categories(draft.machineType).map((category, index) => {
              const row = draft.checklist[index];
              const change = (key, value) =>
                update(
                  "checklist",
                  draft.checklist.map((entry, i) =>
                    i === index ? { ...entry, [key]: value } : entry
                  )
                );
              return (
                <div className="mi-check-row" key={category.key}>
                  <div className="mi-check-heading">
                    <i className={category.icon} />
                    <div>
                      <label htmlFor={`mi-${category.key}`}>
                        {category.label}
                      </label>
                      <small>{category.hint}</small>
                    </div>
                    <Dropdown
                      inputId={`mi-${category.key}`}
                      value={row.rating}
                      options={Object.entries(RATINGS).map(([value, item]) => ({
                        value,
                        label: item.label,
                      }))}
                      onChange={(e) => change("rating", e.value)}
                      placeholder="Seleccionar"
                    />
                  </div>
                  {["regular", "bad"].includes(row.rating) && (
                    <InputTextarea
                      aria-label={`Hallazgo: ${category.label}`}
                      value={row.note}
                      onChange={(e) => change("note", e.target.value)}
                      rows={2}
                      maxLength={2000}
                      autoResize
                      className="w-full mt-3"
                      placeholder="¿Qué encontraste y qué necesita atención? *"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
      {step === 2 && (
        <section className="mi-form-section">
          <h3>Deja evidencia y envía el aviso</h3>
          <p className="mi-muted">
            {machine?.machine || machine?.serialNumber} ·{" "}
            {machine?.operator?.name ||
              "Pendiente de asignación por supervisión"}
          </p>
          <fieldset className="mi-rating-picker">
            <legend>Calificación general *</legend>
            {Object.entries(RATINGS)
              .filter(([key]) => key !== "na")
              .map(([key, value]) => (
                <button
                  type="button"
                  key={key}
                  className={`mi-rating-choice ${key} ${
                    draft.rating === key ? "is-selected" : ""
                  }`}
                  aria-pressed={draft.rating === key}
                  onClick={() => update("rating", key)}
                >
                  <i className={value.icon} />
                  <strong>{value.label}</strong>
                  <small>{value.hint}</small>
                </button>
              ))}
          </fieldset>
          <div className="mi-evidence">
            <PhotoEvidenceUpload
              label="Fotografías de evidencia *"
              value={draft.photos}
              onChange={(photos) => update("photos", photos)}
              maxPhotos={6}
              maxSizeMB={1}
              maxTotalBytes={3 * 1024 * 1024}
              helperText="Al menos una foto. Incluye los problemas detectados. Hasta 6 imágenes, 1 MB por foto y 3 MB en total."
            />
          </div>
          <label htmlFor="mi-comment">Comentario final *</label>
          <InputTextarea
            id="mi-comment"
            value={draft.comment}
            onChange={(e) => update("comment", e.target.value)}
            rows={4}
            maxLength={2000}
            className="w-full"
            placeholder="Describe el estado general y lo que debe atender el operador."
          />
          <div className="mi-notice">
            <i className="pi pi-bell" />
            <span>
              El aviso se enviará al responsable. Las inspecciones malas también
              se notificarán a supervisión. La evaluación quedará guardada en el
              historial.
            </span>
          </div>
        </section>
      )}
      <div className="mi-form-footer">
        <Button
          label={step ? "Atrás" : "Cancelar"}
          icon="pi pi-arrow-left"
          className="p-button-text"
          disabled={saving}
          onClick={
            step
              ? () => {
                  setError("");
                  setStep(step - 1);
                }
              : onCancel
          }
        />
        <span>Paso {step + 1} de 3</span>
        {step < 2 ? (
          <Button
            label="Continuar"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={next}
          />
        ) : (
          <Button
            label="Enviar inspección"
            icon="pi pi-send"
            loading={saving}
            onClick={submit}
          />
        )}
      </div>
    </div>
  );
}
